import { CSSProperties, useEffect, useRef, useState } from "react";
import WithLabel from "../../../Component/WithLabel";
import LandingAutocomplete from "./LandingAutocomplete";
import { searchByTitle, searchResultType } from "../../../Data/Search";
import Autocomplete from "@material-ui/lab/Autocomplete/Autocomplete";
import { computeFuzzyDistance } from "../../../Functions";
import { CircularProgress, InputAdornment, TextField } from "@material-ui/core";
import { useEpisodes } from "../../../Recoil/Title/Episodes";
import WebtoonTodayNormalPopper from "./WebtoonTodayNormalPopper";

import './ShortLinkGenerator.scss';
import { useTitles } from "../../../Recoil/Title/Titles";
// 이 페이지 input 요소의 높이

const SearchTitleForPlatform = ({
    serviceId,
    titleId,
    episodeId,
    disabled = false,
    onSelectTitleId,
    onSelectEpisodeId,
    InputHeight = 50,
    contentStyles,
}:{
    serviceId: string | undefined,
    titleId: string | undefined,
    episodeId: string | undefined,
    disabled?: boolean,
    onSelectTitleId: (searchResult: string | undefined) => void,
    onSelectEpisodeId: (episode: string | undefined) => void,
    InputHeight?: number,
    contentStyles?: CSSProperties
}) => {
    const { fetch: fetchTitles } = useTitles()
    const { fetch: fetchEpisodes, episodeObject } = useEpisodes();
    
    const [ titleList, setTitleList ] = useState<searchResultType[]>([]);
    const [ episodeList, setEpisodeList ] = useState<EpisodeType[]>([]);

    const [ isLoading, setIsLoading ] = useState<boolean>(false);
    
    const titles = titleList.map( ({title}:{title:string}) => title );
    const episodes = ["작품홈"].concat(episodeList.map( ({episodeName}:{episodeName: string}) => episodeName)).filter((episode)=>episode);

    const onWebtoonTitleInputChange = async (title: string ) => {
        setIsLoading(true);
        
        const restitleList = await searchByTitle(title);

        if (!restitleList){
            setTitleList([]);
        } else {
            setTitleList(restitleList.filter((title)=>title.serviceId === serviceId));
        }

        setIsLoading(false);
    }

    useEffect(() => {
        const selectedTitleObj = titleList.find( (title) => title.serviceId === serviceId && title.titleId === titleId );
        if ( selectedTitleObj ) {
            const episodeRes = episodeObject[`${selectedTitleObj.serviceId}:${selectedTitleObj.titleId}`] || []
            if ( episodeRes.length > 0 ){
                setEpisodeList(episodeRes);
            }
        }
    },[episodeObject, serviceId, titleId, titleList])

    useEffect(() => {
        (async () => { 
            if (serviceId && titleId) {
                fetchEpisodes({serviceId, titleId})
            }
        })()
    },[fetchEpisodes, serviceId, titleId]);
    
    const readTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [ open, setOpen ] = useState<boolean>(false);
    const [ text, setText ] = useState<string |undefined>(undefined);

    const filteredTitleOptions = open ? titles : [];
    return(<>
    <WithLabel label={'작품 제목'} contentStyles={contentStyles} >
        <Autocomplete
            autoHighlight
            value={text}
            disabled={!serviceId || disabled}
            forcePopupIcon={false}
            disableClearable={true}
            disabledItemsFocusable
            loading={isLoading}
            loadingText={"검색 결과를 불러오고 있습니다."}
            freeSolo={(!open && !isLoading)}
            noOptionsText={"검색 결과가 없습니다."}
            clearOnBlur={false}
            options={filteredTitleOptions}
            getOptionLabel={(option) => option}
            onChange={ (event, newValue) => {
                setText(newValue);
                ( async () => {
                    const title = titleList.find( title => title.title === newValue );
                    if ( !title ) {
                        return;
                    }
                    onSelectTitleId(title.titleId);

                    fetchTitles([{serviceId: title.serviceId, titleId: title.titleId}])
                    fetchEpisodes({serviceId: title.serviceId, titleId: title.titleId});
                })();
            }}
            onInputChange={(event, value) => {
                if (readTimer.current){
                    clearTimeout(readTimer.current);
                }

                readTimer.current = setTimeout( async () => {
                    onWebtoonTitleInputChange(value);
                }, 300);
                if (value.length > 0 && !open) {
                    setOpen(true);
                }else if (value.length === 0){
                    setText(undefined);
                    setOpen(false);
                }
            }}
            filterOptions={(options, state) => {
                const whiteSpaceReg = /(^\s+)|(\s+(?=\s))/gu;

                const filteredOption = options
                    .map((option) => [option, computeFuzzyDistance(state.inputValue.replace(whiteSpaceReg, ''), option)] as [string, number])
                    .filter(([, distance]) => distance >= 0)
                    .sort(([, distanceA], [, distanceB]) => distanceA - distanceB)
                    .map(([option]) => option);
                return filteredOption;
            }}
            renderInput={(params) => <TextField
                placeholder={"작품 제목 검색"}
                {...params} label="" variant="outlined"
                InputProps={{
                    ...params.InputProps,
                    endAdornment: (<>
                        {open && isLoading ? <CircularProgress color='inherit' size={20} /> : null}
                        <InputAdornment className={'EndAdornmentIcon'} position='end'>
                            {<img src={'https://static.webtoon.today/ddah/icon/search.svg'} alt={'SearchIcon'} style={{paddingRight: 6}} />}
                        </InputAdornment>
                    </>),
                    style: {
                        height: InputHeight
                    },
                }}
                size={'small'}
                disabled={!serviceId || disabled}
                style={(!serviceId || disabled)?{
                    background: "linear-gradient(0deg, rgba(220, 220, 220, 0.32) 0%, rgba(220, 220, 220, 0.32) 100%), #FFF"
                }:{}}
            />}
            PopperComponent={(props) =>  (<>
                {titleList.length > 0 &&<WebtoonTodayNormalPopper {...props} />}
            </>)} 
            ListboxProps={{style:{
                maxHeight: 300
            }}}
        />
    </WithLabel>
    <WithLabel label={'도착 페이지'} contentStyles={contentStyles} >
        <LandingAutocomplete
            options={episodes}
            onChange={ (episode) => {
                const episodeObj = episodeList.find( ({episodeName}) => `${episodeName}` === episode );
                if ( episodeObj ) {
                    onSelectEpisodeId(episodeObj.episodeId);
                }else {
                    onSelectEpisodeId(undefined);
                }
            }}
            placeholder={"선택해주세요."}
            disabled={!titleId || !serviceId || disabled}
            InputProps={{
                style: {
                    height: InputHeight
                }
            }}
            key={titleId}
        />
    </WithLabel></>)
}

export default SearchTitleForPlatform;